import { ref, watch, onMounted, onUnmounted, isRef } from 'vue';

/**
 * 🚀 Composable INTELIGENTE para auto-guardar formularios
 * 
 * Características:
 * - ⚡ Guardado INMEDIATO al escribir (3 segundos después de dejar de escribir)
 * - Protección total contra pérdida de datos (beforeunload)
 * - MERGE INTELIGENTE - siempre preserva lo más reciente
 * - ⏱Comparación de timestamps para evitar sobrescribir datos nuevos
 * - Guardado automático de respaldo cada 30 segundos
 * 
 * @param {Object} formData - Reactive form data to autosave (ref or reactive)
 * @param {String|ComputedRef} storageKey - Unique key for localStorage (can be computed)
 * @param {Number} interval - Autosave interval in milliseconds (default: 30000 = 30s)
 * @param {Array} fieldsToSave - Array of field names to save (if empty, saves all)
 */
export function useAutosave(formData, storageKey, interval = 30000, fieldsToSave = []) {
    const lastSaveTime = ref(null);
    const hasUnsavedChanges = ref(false);
    const hasDraft = ref(false);
    const isSaving = ref(false);

    let autosaveTimer = null;
    let debounceTimer = null;
    let lastDataSnapshot = null;

    // Función para obtener la clave actual (maneja strings y computed)
    const getStorageKey = () => {
        return isRef(storageKey) ? storageKey.value : storageKey;
    };

    /**
     * 🧠 Comparar si los datos actuales son diferentes del último snapshot
     */
    const hasDataChangedFromSnapshot = (currentData) => {
        if (!lastDataSnapshot) return true;
        return JSON.stringify(currentData) !== JSON.stringify(lastDataSnapshot);
    };

    /**
     * 💾 GUARDADO INTELIGENTE - Siempre preserva lo más reciente
     * Compara timestamps y hace merge inteligente
     */
    const saveDraft = () => {
        const data = formData.value || formData;
        if (!data) return;

        try {
            isSaving.value = true;
            const key = getStorageKey();
            const now = new Date();

            // Extraer campos a guardar
            let dataToSave = fieldsToSave.length > 0
                ? Object.fromEntries(
                    fieldsToSave.map(field => [field, data[field]])
                )
                : { ...data };

            // Verificar si hay cambios reales
            if (!hasDataChangedFromSnapshot(dataToSave)) {
                isSaving.value = false;
                return;
            }

            // Cargar draft existente para comparar timestamps
            const existingDraft = localStorage.getItem(key);

            if (existingDraft) {
                try {
                    const existing = JSON.parse(existingDraft);

                    // MERGE INTELIGENTE: Combinar datos preservando lo más reciente por campo
                    const mergedData = { ...existing.data };

                    Object.keys(dataToSave).forEach(field => {
                        const currentValue = dataToSave[field];
                        const existingValue = existing.data[field];

                        // Lógica de merge inteligente:
                        // 1. Si el campo actual tiene contenido y el existente no, usar el actual
                        // 2. Si el campo actual no tiene contenido pero el existente sí, preservar el existente
                        // 3. Si ambos tienen contenido, usar el más largo (probablemente más completo)
                        if (currentValue && !existingValue) {
                            mergedData[field] = currentValue;
                        } else if (!currentValue && existingValue) {
                            mergedData[field] = existingValue;
                        } else if (currentValue && existingValue) {
                            const currentLength = String(currentValue).length;
                            const existingLength = String(existingValue).length;
                            mergedData[field] = currentLength > existingLength ? currentValue : existingValue;
                        }
                    });

                    dataToSave = mergedData;
                } catch (e) {
                    console.warn('⚠️ Error al parsear draft existente, sobrescribiendo:', e);
                }
            }

            // Crear nuevo draft con timestamp actual
            const draft = {
                data: dataToSave,
                timestamp: now.toISOString(),
                version: '2.0', // v2.0 con merge inteligente
                lastModified: now.getTime()
            };

            // Guardar en localStorage
            localStorage.setItem(key, JSON.stringify(draft));

            // Actualizar estado
            lastSaveTime.value = now;
            hasUnsavedChanges.value = false;
            lastDataSnapshot = { ...dataToSave };


        } catch (error) {
            console.error('❌ Error al guardar draft:', error);
        } finally {
            isSaving.value = false;
        }
    };

    /**
     * ⚡ Guardado con debounce - Se activa 3 segundos después de dejar de escribir
     */
    const debouncedSave = () => {
        // Limpiar timer anterior
        if (debounceTimer) {
            clearTimeout(debounceTimer);
        }

        // Nuevo timer de 3 segundos
        debounceTimer = setTimeout(() => {
            saveDraft();
        }, 3000); // 3 segundos después de dejar de escribir
    };

    /**
     * 📂 Cargar borrador desde localStorage
     */
    const loadDraft = () => {
        try {
            const key = getStorageKey();
            const saved = localStorage.getItem(key);
            if (!saved) {
                hasDraft.value = false;
                return null;
            }

            const draft = JSON.parse(saved);
            hasDraft.value = true;
            lastSaveTime.value = new Date(draft.timestamp);
            lastDataSnapshot = { ...draft.data };


            return draft.data;
        } catch (error) {
            hasDraft.value = false;
            return null;
        }
    };

    /**
     * 🗑️ Descartar borrador
     */
    const discardDraft = () => {
        const key = getStorageKey();
        localStorage.removeItem(key);
        hasDraft.value = false;
        lastSaveTime.value = null;
        hasUnsavedChanges.value = false;
        lastDataSnapshot = null;
    };

    /**
     * ⏰ Formatear tiempo de guardado
     */
    const getFormattedSaveTime = () => {
        if (!lastSaveTime.value) return '';

        const now = new Date();
        const diff = now - lastSaveTime.value;
        const seconds = Math.floor(diff / 1000);
        const minutes = Math.floor(seconds / 60);

        if (seconds < 60) {
            return 'hace unos segundos';
        } else if (minutes < 60) {
            return `hace ${minutes} minuto${minutes > 1 ? 's' : ''}`;
        } else {
            return lastSaveTime.value.toLocaleTimeString('es-ES', {
                hour: '2-digit',
                minute: '2-digit'
            });
        }
    };

    /**
     * 🔄 Iniciar auto-guardado periódico (backup cada 30s)
     */
    const startAutosave = () => {
        if (autosaveTimer) {
            clearInterval(autosaveTimer);
        }

        autosaveTimer = setInterval(() => {
            if (hasUnsavedChanges.value && !isSaving.value) {
                saveDraft();
            }
        }, interval);
    };

    /**
     * ⏸️ Detener auto-guardado
     */
    const stopAutosave = () => {
        if (autosaveTimer) {
            clearInterval(autosaveTimer);
            autosaveTimer = null;
        }
        if (debounceTimer) {
            clearTimeout(debounceTimer);
            debounceTimer = null;
        }
    };

    /**
     * 🔒 Protección contra pérdida de datos al cerrar/recargar
     */
    const handleBeforeUnload = (event) => {
        if (hasUnsavedChanges.value && !isSaving.value) {
            saveDraft();

            // Mensaje de confirmación (algunos navegadores lo muestran)
            const message = 'Tienes cambios sin guardar. ¿Seguro que quieres salir?';
            event.returnValue = message;
            return message;
        }
    };

    /**
     * 👀 Watch para detectar cambios - Activa guardado con debounce
     */
    watch(
        () => formData.value || formData,
        (newData) => {
            // ✅ NO auto-guardar si es un formulario nuevo (sin ID)
            // Esto evita crear borradores cuando se agregan medicamentos o plantillas
            // antes de crear la receta por primera vez
            if (!newData || !newData.id) {
                return; // Ignorar cambios en formularios nuevos
            }

            const dataToCheck = fieldsToSave.length > 0
                ? Object.fromEntries(
                    fieldsToSave.map(field => [field, newData[field]])
                )
                : { ...newData };

            if (hasDataChangedFromSnapshot(dataToCheck)) {
                hasUnsavedChanges.value = true;
                debouncedSave(); // ⚡ Guardado rápido con debounce
            }
        },
        { deep: true }
    );

    /**
     * 🚀 Lifecycle hooks
     */
    onMounted(() => {
        // Iniciar auto-guardado periódico
        startAutosave();

        // Agregar listener para beforeunload (protección al cerrar)
        window.addEventListener('beforeunload', handleBeforeUnload);
    });

    onUnmounted(() => {
        // Guardar antes de desmontar si hay cambios
        if (hasUnsavedChanges.value && !isSaving.value) {
            saveDraft();
        }

        // Detener auto-guardado
        stopAutosave();

        // Remover listener de beforeunload
        window.removeEventListener('beforeunload', handleBeforeUnload);
    });

    return {
        // Estado
        lastSaveTime,
        hasUnsavedChanges,
        hasDraft,
        isSaving,

        // Métodos
        saveDraft,
        loadDraft,
        discardDraft,
        getFormattedSaveTime,
        startAutosave,
        stopAutosave
    };
}
